import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, Bell, Mail, MessageSquare, Phone, Search, X } from "lucide-react";
import { RunningIcon } from "@/assets/icons/RunningIcon";
import { useTranslation } from "react-i18next";
import { useClientSearch } from "@/shared/hooks/useClientSearch";
import { useNotificationCount } from "@/shared/hooks/useNotificationCount";
import { useScheduledRunsCount } from "@/shared/hooks/useScheduledRunsCount";
import { useUnreadMessagesCount } from "@/shared/hooks/useUnreadMessagesCount";
import { useEmergencyCount } from "@/shared/hooks/useEmergencyCount";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { PERMISSIONS } from "@/shared/constants/permissions";
import CurrentTime from "./CurrentTime";
import { useTenant } from "@/shared/hooks/useTenant";
import { useAuth } from "@/shared/providers/AuthProvider";
import { useRuntimeConfigContext } from "@/shared/providers/RuntimeConfigProvider";
import { useAppearance } from "@/shared/providers/AppearanceProvider";
import { useUpdateUser } from "@/features/system/hooks/useUpdateUser";
import { useToast } from "@/shared/contexts/ToastContext";
import ChangePasswordModal from "@/features/system/components/userManagement/ChangePasswordModal";
import { ViewSettingsModal } from "@/shared/components/viewSettings";
import SendFeedbackModal from "./SendFeedbackModal";
import { useSendFeedback } from "./hooks/useSendFeedback";
import logoWhite from "@/assets/ecolaneWebWhite.svg";
import transitLogo from "@/assets/transit_logo.svg";
import NavMenu from "./NavMenu";
import { getNavItems, menuRoutePrefixes } from "./navConfig.ts";
import { getAccessibleNavConfig } from "@/shared/utils/nav.utils.ts";
import { AppRoutes } from "@/app/routes";
import { env } from "@/config/env";
import { NavItem } from "./types";
import "./Navbar.css";

const Navbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { setAppearance } = useAppearance();
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { clients, isLoading } = useClientSearch(searchTerm, isSearchOpen);
  const { hasPermission } = usePermissions();
  const canViewAlerts = hasPermission(PERMISSIONS.ALERT_VIEW);
  // Mirrors legacy nav/_batch_optimize_status.haml:1 — the scheduled-runs
  // (trip-optimization) indicator is shown only to users who can optimize
  // routes or trip groups.
  const canViewScheduledRuns =
    hasPermission(PERMISSIONS.METAROUTE_OPTIMIZE_OPERATION) ||
    hasPermission(PERMISSIONS.TRIP_GROUP_OPTIMIZE_OPERATION);
  // Gate polling on permission — mirrors legacy alert_status.es6:155 /
  // batch_optimize_status.es6:153 where the poller only starts when the
  // (permission-gated) indicator element is present in the DOM.
  const { notificationCount } = useNotificationCount(canViewAlerts);
  const { scheduledRunsCount } = useScheduledRunsCount(canViewScheduledRuns);
  const { unreadMessagesCount } = useUnreadMessagesCount(canViewAlerts);
  const { emergencyCount } = useEmergencyCount(canViewAlerts);
  const { tenantId } = useTenant();
  const { user, permissions, logout } = useAuth();
  const { config, getConfigValue, isFeatureEnabled } = useRuntimeConfigContext();
  const { showToast } = useToast();
  const { mutate: updateUser } = useUpdateUser();
  const { mutateAsync: submitFeedback } = useSendFeedback();
  const [isChangePasswordOpen, setChangePasswordOpen] = useState(false);
  const [isViewSettingsOpen, setViewSettingsOpen] = useState(false);
  const [isSendFeedbackOpen, setSendFeedbackOpen] = useState(false);
  const currentUsername = user?.login ?? user?.name ?? "User";

  // Determine which logo to use based on tenant
  const logo = tenantId === "metro-transit" ? transitLogo : logoWhite;

  const navItems = React.useMemo(() => getNavItems(config, user), [config, user, i18n.language]);

  const accessibleNavItems = React.useMemo(() => {
    if (!permissions) {
      return {
        operations: [],
        administration: [],
        tools: [],
        system: [],
        user: navItems.user,
      };
    }
    return {
      operations: getAccessibleNavConfig(navItems.operations, permissions),
      administration: getAccessibleNavConfig(navItems.administration, permissions),
      tools: getAccessibleNavConfig(navItems.tools, permissions),
      system: getAccessibleNavConfig(navItems.system, permissions),
      user: navItems.user,
    };
  }, [navItems, permissions]);

  const handleMenuToggle = (menuId: string) => {
    setOpenMenu(openMenu === menuId ? null : menuId);
  };

  const handleMenuClose = () => {
    setOpenMenu(null);
  };

  const handleSearchClose = () => {
    setIsSearchOpen(false);
    setSearchTerm("");
  };

  const handleClientClick = (clientId: number) => {
    handleSearchClose();
    navigate(`${AppRoutes.admin.clients}/${clientId}`);
  };

  const handleSearchEnter = () => {
    const term = searchTerm.trim();
    if (isLoading) return;

    handleSearchClose();

    if (!term) {
      // Empty search — navigate to clients page with ENABLED status filter
      navigate(`${AppRoutes.admin.clients}?q.status=ENABLED`);
    } else if (clients.length === 1) {
      // Single exact match — navigate directly to client details
      navigate(`${AppRoutes.admin.clients}/${clients[0].id}`);
    } else {
      // No match OR multiple matches — navigate to clients page with search string pre-filled
      navigate(`${AppRoutes.admin.clients}?q.status=ENABLED&q.quicksearch=${encodeURIComponent(term)}`);
    }
  };

  const handleChangePassword = async (password: string) => {
    if (!user) return;
    return new Promise<void>((resolve, reject) => {
      updateUser(
        {
          userId: Number(user.id),
          payload: {
            id: Number(user.id),
            login: currentUsername,
            password: password,
          },
          // ECOPD-14897: This is always the current user changing their own
          // password. Pass reloginAs so useUpdateUser.onSuccess re-authenticates
          // with the new password before query refetches fire — keeping the user
          // logged in without requiring them to visit /login.
          reloginAs: { login: currentUsername, password },
        },
        {
          onSuccess: () => {
            showToast(t("system.userManagement.users.changePasswordSuccess"), "success");
            resolve();
          },
          onError: (error) => {
            showToast(error.message, "error");
            reject(error);
          },
        }
      );
    });
  };

  const handleSendFeedback = async ({
    comment,
    attachments,
  }: {
    comment: string;
    attachments: File[];
  }) => {
    try {
      await submitFeedback({
        comment,
        attachments,
        url: window.location.href,
        category: "general",
      });
      showToast(t("navbar.user.sendFeedback") + " – " + t("common.changesSavedSuccess"), "success");
    } catch (error) {
      const errorMessage = (error as Error)?.message || t("common.error");
      showToast(errorMessage, "error");
      throw error;
    }
  };

  const normalizePath = (path: string) => {
    if (!path) return "/";
    const normalized = path.split(/[?#]/)[0].replace(/\/+$/, "").toLowerCase();
    return normalized || "/";
  };

  const isPathMatch = (targetPath: string, currentPath: string) => {
    if (!targetPath || targetPath === "#") {
      return false;
    }

    const normalizedTarget = normalizePath(targetPath);
    const normalizedCurrent = normalizePath(currentPath);

    if (
      normalizedCurrent === normalizedTarget ||
      normalizedCurrent.startsWith(`${normalizedTarget}/`)
    ) {
      return true;
    }

    const embeddedIndex = normalizedCurrent.indexOf(normalizedTarget);
    if (embeddedIndex === -1) {
      return false;
    }

    const hasSegmentBoundaryBefore =
      embeddedIndex === 0 || normalizedCurrent[embeddedIndex - 1] === "/";
    const afterIndex = embeddedIndex + normalizedTarget.length;
    const hasSegmentBoundaryAfter =
      afterIndex === normalizedCurrent.length || normalizedCurrent[afterIndex] === "/";

    return hasSegmentBoundaryBefore && hasSegmentBoundaryAfter;
  };

  const hasActiveRoute = (items: NavItem[], currentPath: string): boolean => {
    return items.some((item) => {
      if (isPathMatch(item.to, currentPath)) {
        return true;
      }

      if (!item.submenu) {
        return false;
      }

      return hasActiveRoute(item.submenu, currentPath);
    });
  };

  const isMenuPrefixActive = (menuId: string, currentPath: string): boolean => {
    const prefixes = menuRoutePrefixes[menuId] ?? [];
    return prefixes.some((prefix) => isPathMatch(prefix, currentPath));
  };

  // Focus the search input when the search overlay opens (replaces autoFocus prop)
  useEffect(() => {
    if (isSearchOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isSearchOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== "Escape") {
        return;
      }

      if (isSearchOpen) {
        handleSearchClose();
      }

      if (openMenu) {
        handleMenuClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isSearchOpen, openMenu]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".navbar-menu-container")) {
        handleMenuClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header>
      {/* Skip navigation link — visually hidden until focused (WCAG 2.4.1 Bypass Blocks) */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[999] focus:bg-surface focus:text-text focus:px-4 focus:py-2 focus:rounded focus:outline-none focus:ring-2 focus:ring-primary"
      >
        Skip to main content
      </a>
    {/*
     * Figma spec: Top Menu_System container — 1440×64px, padding L/R 32px,
     * justify space-between, bg #0C1F45, drop shadow X:0 Y:2 Blur:3 Spread:0 #000 24%.
     */}
    <nav className="sticky top-0 h-16 z-[210] bg-header" aria-label="Main navigation">
      {/* px-8 on desktop, reduced to px-3 on small screens to prevent overflow */}
      <div className="max-w-full mx-auto px-8 py-3">
        <div className="flex items-center gap-2 min-w-0">
          {/* Left: logo + nav menus — shrink-0 so they never compress */}
          <div className="flex items-center gap-4 shrink-0" style={{ height: "40px" }}>
            {/* Figma spec: Frame 16 — 120×40px wrapper, image 106×24px centered (8px top/bottom, 7px left/right) */}
            <Link to="/" onClick={handleMenuClose} aria-label="Go to home page" className="flex items-center justify-center" style={{ width: "120px", height: "40px" }}>
              <img src={logo} alt="Logo" className="object-contain" style={{ width: "106px", height: "24px" }} />
            </Link>
            {/* Navigation menu group — no role="menubar" since NavMenu items are not role="menuitem" */}
            <div className="hidden md:flex items-center gap-2">
              <NavMenu
                title={t("navbar.menu.operations")}
                menuId="operations"
                items={accessibleNavItems.operations}
                openMenu={openMenu}
                isRouteActive={
                  location.state?.from === 'operations' ||
                  (location.state?.from !== 'client' && (
                    hasActiveRoute(accessibleNavItems.operations, location.pathname) ||
                    isMenuPrefixActive("operations", location.pathname)
                  ))
                }
                onToggle={handleMenuToggle}
                onClose={handleMenuClose}
              />
              <NavMenu
                title={t("navbar.menu.administration")}
                menuId="admin"
                items={accessibleNavItems.administration}
                openMenu={openMenu}
                isRouteActive={
                  location.state?.from !== 'operations' && (
                    (location.state?.from === 'client' && (
                      hasActiveRoute(accessibleNavItems.operations, location.pathname) ||
                      isMenuPrefixActive("operations", location.pathname)
                    )) ||
                    hasActiveRoute(accessibleNavItems.administration, location.pathname) ||
                    isMenuPrefixActive("admin", location.pathname)
                  )
                }
                onToggle={handleMenuToggle}
                onClose={handleMenuClose}
              />
              <NavMenu
                title={t("navbar.menu.tools")}
                menuId="tools"
                items={accessibleNavItems.tools}
                openMenu={openMenu}
                isRouteActive={
                  hasActiveRoute(accessibleNavItems.tools, location.pathname) ||
                  isMenuPrefixActive("tools", location.pathname)
                }
                onToggle={handleMenuToggle}
                onClose={handleMenuClose}
              />
              <NavMenu
                title={t("navbar.menu.system")}
                menuId="system"
                items={accessibleNavItems.system}
                openMenu={openMenu}
                isRouteActive={
                  hasActiveRoute(accessibleNavItems.system, location.pathname) ||
                  isMenuPrefixActive("system", location.pathname)
                }
                onToggle={handleMenuToggle}
                onClose={handleMenuClose}
              />
            </div>
          </div>
          {/* Right: search + time + icons + user — fills remaining space, allows search to flex.
           * gap-1 keeps icon buttons tight on narrow viewports */}
          <div className="flex items-center min-w-0 flex-1 justify-end right-navbar">
            {/* Figma: search bar — ideal 346×32px, radius 54px, bg white/16%.
             * flex-1 max-w-[346px] min-w-[80px]: targets Figma's 346px at ≥1366px
             * but shrinks gracefully on narrower viewports (e.g. 1280px + sidebar)
             * preventing horizontal overflow. shrink-0 is intentionally absent. */}
            <button
              className="flex items-center gap-[10px] bg-white/[0.16] hover:bg-white/25 text-on-primary rounded-[54px] px-3 py-2 flex-1 max-w-[346px] min-w-[80px] h-8 text-sm font-normal leading-none tracking-[0px] transition-colors focus:outline-none focus:ring-2 focus:ring-white/40"
              style={{ fontFamily: "Inter, sans-serif" }}
              aria-label="Search"
              onClick={() => setIsSearchOpen(true)}
            >
              <Search size={16} className="shrink-0" aria-hidden="true" />
              <span className="font-normal truncate">Search</span>
            </button>
            {/* 16px gap: search → time */}
            <div className="shrink-0 ml-4">
              <CurrentTime />
            </div>
            {/* Icons group — gap-2 (8px) between icons, ml-4 (16px) from time capsule.
             * Grouping handles conditional rendering cleanly. */}
            <div className="flex items-center gap-2 ml-4">
              {/* Order mirrors legacy app: runs → emergency → alerts → messages
               * Figma: each icon button is 40×40px (Frames 1000005615 & 1000005614). */}
              {canViewScheduledRuns && scheduledRunsCount > 0 && (
                <div className="navbar-tooltip navbar-tooltip-bottom">
                  {/* Figma: 40×40px icon button */}
                  <button
                    className="text-on-primary flex items-center justify-center p-3"
                    aria-label={t("navbar.scheduledRuns", {
                      count: scheduledRunsCount,
                      defaultValue: "Scheduled runs: {{count}}",
                    })}
                    aria-describedby="navbar-tooltip-scheduled-runs"
                    onClick={() =>
                      navigate(
                        "/system/batch-jobs?q.run_asap=1&q.enabled=no_filter&q.range_default_enum=no_filter"
                      )
                    }
                    data-testid="navbar-scheduled-runs-button"
                  >
                    <div className="relative inline-flex items-center">
                      <RunningIcon width={16} height={16} aria-hidden="true" />
                      <span
                        className="absolute -top-2 -right-3 min-w-[16px] h-[16px] px-1 bg-info text-white text-[10px] font-semibold leading-[16px] text-center rounded-full pointer-events-none"
                        data-testid="navbar-scheduled-runs-count"
                      >
                        {scheduledRunsCount > 99 ? "99+" : scheduledRunsCount}
                      </span>
                    </div>
                  </button>
                  <span id="navbar-tooltip-scheduled-runs" className="navbar-tooltip-text" role="tooltip">
                    {t("navbar.scheduledTasksTooltip", {
                      count: scheduledRunsCount,
                      defaultValue: "Scheduled tasks: {{count}}",
                    })}
                  </span>
                </div>
              )}
              {canViewAlerts && emergencyCount > 0 && (
                <div className="navbar-tooltip navbar-tooltip-bottom">
                  {/* Figma: 40×40px icon button */}
                  <button
                    className="text-on-primary flex items-center justify-center p-3"
                    aria-label={t("navbar.emergency", {
                      count: emergencyCount,
                      defaultValue: "Emergency alerts: {{count}}",
                    })}
                    aria-describedby="navbar-tooltip-emergency"
                    onClick={() =>
                      navigate("/operations/alerts?keep=1&q.alert_group=emerg&q.status=unresolved")
                    }
                    data-testid="navbar-emergency-button"
                  >
                    <div className="relative inline-block">
                      <Bell size={16} aria-hidden="true" />
                      <span
                        className="absolute -top-2 -right-3 min-w-[16px] h-[16px] px-1 bg-error text-white text-[10px] font-semibold leading-[16px] text-center rounded-full pointer-events-none"
                        data-testid="navbar-emergency-count"
                      >
                        {emergencyCount > 99 ? "99+" : emergencyCount}
                      </span>
                    </div>
                  </button>
                  <span id="navbar-tooltip-emergency" className="navbar-tooltip-text" role="tooltip">
                    {t("navbar.emergencyTooltip", {
                      count: emergencyCount,
                      defaultValue: "Emergency: {{count}}",
                    })}
                  </span>
                </div>
              )}
              {/* Mirrors legacy nav/_alert_status.haml:22 — alerts indicator
                  requires alert_view (can?(:read, Alert)). */}
              {canViewAlerts && (
                <div className="navbar-tooltip navbar-tooltip-bottom">
                  {/* Figma: 40×40px icon button */}
                  <button
                    className="text-on-primary flex items-center justify-center p-3"
                    aria-label={t("navbar.notifications", {
                      count: notificationCount,
                      defaultValue: "Notifications: {{count}} unread",
                    })}
                    aria-describedby="navbar-tooltip-alerts"
                    onClick={() =>
                      navigate(
                        "/operations/alerts?keep=1&q.range_default_enum=today&q.status=unresolved"
                      )
                    }
                  >
                    <div className="relative inline-block">
                      <AlertTriangle size={16} aria-hidden="true" />
                      {notificationCount > 0 && (
                        <span
                          className="absolute -top-2 -right-3 min-w-[16px] h-[16px] px-1 bg-warning text-white text-[10px] font-semibold leading-[16px] text-center rounded-full pointer-events-none"
                          data-testid="navbar-notification-count"
                        >
                          {notificationCount > 99 ? "99+" : notificationCount}
                        </span>
                      )}
                    </div>
                  </button>
                  <span id="navbar-tooltip-alerts" className="navbar-tooltip-text" role="tooltip">
                    {t("navbar.alertsTooltip", {
                      count: notificationCount,
                      defaultValue: "Alerts: {{count}}",
                    })}
                  </span>
                </div>
              )}
              {canViewAlerts && unreadMessagesCount > 0 && (
                <div className="navbar-tooltip navbar-tooltip-bottom">
                  {/* Figma: 40×40px icon button */}
                  <button
                    className="text-on-primary flex items-center justify-center p-3"
                    aria-label={t("navbar.unreadMessages", {
                      count: unreadMessagesCount,
                      defaultValue: "Unread messages: {{count}}",
                    })}
                    aria-describedby="navbar-tooltip-messages"
                    onClick={() =>
                      navigate(
                        "/operations/messages?keep=1&q.handle_status=unread&q.range_default_enum=today"
                      )
                    }
                    data-testid="navbar-unread-messages-button"
                  >
                    <div className="relative inline-block">
                      <MessageSquare size={16} aria-hidden="true" />
                      <span
                        className="absolute -top-2 -right-3 min-w-[16px] h-[16px] px-1 bg-info text-white text-[10px] font-semibold leading-[16px] text-center rounded-full pointer-events-none"
                        data-testid="navbar-unread-messages-count"
                      >
                        {unreadMessagesCount > 99 ? "99+" : unreadMessagesCount}
                      </span>
                    </div>
                  </button>
                  <span id="navbar-tooltip-messages" className="navbar-tooltip-text" role="tooltip">
                    {t("navbar.messagesTooltip", {
                      count: unreadMessagesCount,
                      defaultValue: "Messages: {{count}}",
                    })}
                  </span>
                </div>
              )}
            </div>
            {/* 16px gap: icons group → user menu */}
            <div className="ml-4 flex gap-2 items-center">
              <NavMenu
                title={currentUsername}
                menuId="userMenu"
                items={accessibleNavItems.user.map((item) => {
                  if (item.key === "user-feedback") {
                    return {
                      ...item,
                      onClick: () => {
                        setSendFeedbackOpen(true);
                        handleMenuClose();
                      },
                    };
                  }
                  if (item.key === "change-password") {
                    return { ...item, onClick: () => setChangePasswordOpen(true) };
                  }
                  if (item.key === "view-settings") {
                    return { ...item, onClick: () => setViewSettingsOpen(true) };
                  }
                  if (item.key === "user-signout") {
                    return {
                      ...item,
                      onClick: () => {
                        handleMenuClose();
                        logout();
                      },
                    };
                  }
                  if (item.key === "user-version-info") {
                    const backendVersion = getConfigValue<string>("system.version.number") ?? "";
                    return {
                      ...item,
                      version: backendVersion,
                      frontendVersion: env.APP_VERSION,
                      reactVersion: React.version,
                    };
                  }
                  // Wire Switch to older version — only shown when feature flag is on
                  if (item.key === "user-switch-version") {
                    if (!isFeatureEnabled("web.ccagent.feature.showRebootToLegacySwitch")) {
                      return null as unknown as NavItem;
                    }
                    return {
                      ...item,
                      onClick: () => {
                        handleMenuClose();
                        const drtBaseUrl = getConfigValue<string>("rails.drt.baseUrl");
                        let url: string;
                        if (drtBaseUrl) {
                          url = drtBaseUrl.startsWith("http") ? drtBaseUrl : `https://${drtBaseUrl}`;
                        } else {
                          const primaryHost =
                            getConfigValue<string>("supervisor.frontend.primaryhost") || env.OLD_APP_URL;
                          url = primaryHost.startsWith("http")
                            ? `${primaryHost}/drt`
                            : `https://${primaryHost}/drt`;
                        }
                        window.location.href = url;
                      },
                    };
                  }
                  // Wire Language submenu items with i18n change
                  if (item.key === "user-language") {
                    return {
                      ...item,
                      submenu: item.submenu?.map((langItem) => ({
                        ...langItem,
                        onClick: () => {
                          const langCode = langItem.key.replace("lang-", "");
                          i18n.changeLanguage(langCode);
                          localStorage.setItem("lang", langCode);
                          handleMenuClose();
                        },
                      })),
                    };
                  }
                  // Wire Theme submenu items with appearance change
                  if (item.key === "user-theme") {
                    return {
                      ...item,
                      submenu: item.submenu?.map((themeItem) => ({
                        ...themeItem,
                        onClick: () => {
                          const themeValue = themeItem.key.replace("theme-", "") as "light" | "dark" | "system";
                          setAppearance(themeValue);
                          handleMenuClose();
                        },
                      })),
                    };
                  }
                  return item;
                }).filter((item): item is NavItem => item !== null)}
                isUserMenu
                openMenu={openMenu}
                isRouteActive={false}
                onToggle={handleMenuToggle}
                onClose={handleMenuClose}
              />
            </div>
          </div>
        </div>
      </div>

      <ChangePasswordModal
        isOpen={isChangePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
        username={currentUsername}
        onChangePassword={handleChangePassword}
      />

      <ViewSettingsModal isOpen={isViewSettingsOpen} onClose={() => setViewSettingsOpen(false)} />

      <SendFeedbackModal
        isOpen={isSendFeedbackOpen}
        onClose={() => setSendFeedbackOpen(false)}
        onSubmit={handleSendFeedback}
      />

      {isSearchOpen && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-20"
          onClick={handleSearchClose}
          aria-hidden="true"
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="client-search-dialog-title"
            className="bg-surface rounded-lg shadow-lg w-full max-w-2xl mx-4 max-h-[600px] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 p-4 border-b border-border">
              <Search size={20} className="text-text-muted" aria-hidden="true" />
              {/* Visually hidden label for screen readers — placeholder alone is not sufficient (WCAG 1.3.1) */}
              <label id="client-search-dialog-title" htmlFor="navbar-client-search" className="sr-only">
                Search for clients
              </label>
              <input
                ref={searchInputRef}
                id="navbar-client-search"
                type="text"
                placeholder="Search for clients by name, phone, or email..."
                className="flex-1 bg-transparent border-none outline-none text-text placeholder:text-text-subtle text-base h-8 p-2"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSearchEnter(); }}
                aria-describedby="navbar-search-hint"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
              />
              <button
                onClick={handleSearchClose}
                className="text-text-muted hover:text-text focus:outline-none focus:ring-2 focus:ring-primary rounded"
                aria-label="Close search"
                type="button"
              >
                <X size={20} aria-hidden="true" />
              </button>
            </div>
            {/* Hidden hint for screen readers */}
            <p id="navbar-search-hint" className="sr-only">
              Type a name, phone number, or email address to search for clients. Results will appear below.
            </p>

            <div className="flex-1 overflow-y-auto" aria-live="polite" aria-atomic="false">
              {searchTerm && searchTerm.length >= 2 && !isLoading && (
                <span className="sr-only">
                  {clients.length === 0
                    ? "No clients found"
                    : `${clients.length} result${clients.length === 1 ? "" : "s"} found`}
                </span>
              )}
              {!searchTerm || searchTerm.length < 2 ? (
                <div className="p-6 text-center">
                  <div className="flex flex-col items-center justify-center gap-4">
                    <Search size={64} className="text-text-muted/30" aria-hidden="true" />
                    <div>
                      <p className="text-lg font-medium text-text mb-2">Search for clients</p>
                      <p className="text-sm text-text-muted">
                        Type a name, phone number, or email to find clients
                      </p>
                    </div>
                  </div>
                </div>
              ) : isLoading ? (
                <div className="p-6 text-center">
                  <p className="text-sm text-text-muted">Searching...</p>
                </div>
              ) : clients.length === 0 ? (
                <div className="p-6 text-center">
                  <p className="text-sm text-text-muted">No clients found</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {clients.map((client) => {
                    const getInitials = (name: string) => {
                      const parts = name.trim().split(" ");
                      if (parts.length >= 2) {
                        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
                      }
                      return name.substring(0, 2).toUpperCase();
                    };

                    const initials = getInitials(client.name);

                    return (
                      <button
                        key={client.id}
                        onClick={() => handleClientClick(client.id)}
                        className="w-full px-4 py-3 hover:bg-surface-alt transition-colors text-left"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm bg-primary">
                            {initials}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-text mb-0.5">{client.name}</div>
                            <div className="flex items-center gap-3 text-text-muted text-sm">
                            <div className="flex items-center gap-1">
                              <Phone size={14} aria-hidden="true" />
                              <span>{client.phone_mobile || client.phone_landline || "N/A"}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Mail size={14} aria-hidden="true" />
                              <span className="max-w-[280px] truncate">
                                {client.email || "N/A"}
                              </span>
                            </div></div>
                          </div>

                          {/* <div className="flex items-center gap-3 text-text-muted text-sm">
                            <div className="flex items-center gap-1">
                              <Phone size={14} aria-hidden="true" />
                              <span>{client.phone_mobile || client.phone_landline || "N/A"}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Mail size={14} aria-hidden="true" />
                              <span className="max-w-[200px] truncate">``
                                {client.email || "N/A"}
                              </span>
                            </div>
                          </div> */}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
    </header>
  );
};

export default Navbar;
